import { ValidationError } from 'yup';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { Employees, ReschedulePolicy } from '@prisma/client';
import {
  addBusinessTrip,
  createBusinessService,
  updateBusiness,
  updateBusinessService,
  updateBusinessSettings,
} from '../../services/business';
import {
  AddBusinessTripPayload,
  BusinessService,
  ONLINE_BOOKINGS_BEHAVIOR,
  UpdateBusinessPayload,
  UpdateBusinessSettingsPayload,
  UpsertBusinessServiceInitValues,
  addBusinessTripSchema,
  updateBusinessSchema,
  updateBusinessSettingsSchema,
  upsertBusinessServiceFormSchema,
  upsertBusinessServiceSchema,
} from '../../types/businesses';
import {
  Trip,
  Business,
  LoadedUser,
  Employee,
  EmployeeToBusiness,
  Service,
} from '../../interfaces';
import { CUSTOMER_WEB_URL } from '../../constants/global';
import { isSudoManager } from '../../utils/global/helper';

export async function handleUpdateBusiness(
  businessId: string,
  payload: UpdateBusinessPayload,
) {
  try {
    const body = await updateBusinessSchema.noUnknown().validate(payload, {
      stripUnknown: true,
    });

    const updatedBusiness = await updateBusiness(businessId, body);

    if (updatedBusiness) {
      toast.success('Business updated!');
    }

    return updatedBusiness;
  } catch (error) {
    if (error instanceof ValidationError) {
      toast.error(error.message);
    } else if (error instanceof AxiosError && error?.response?.status === 403) {
      toast.error('You do not have permission to update this business');
    } else toast.error('Failed to update business');
    return null;
  }
}

export async function handleAddBusinessTrip(
  businessId: string,
  payload: AddBusinessTripPayload,
) {
  try {
    const body = await addBusinessTripSchema.noUnknown().validate(payload, {
      stripUnknown: true,
    });

    const trip = await addBusinessTrip(businessId, body);

    if (trip) {
      toast.success('Trip added!');
    }

    return trip;
  } catch (error) {
    if (error instanceof ValidationError) {
      toast.error(error.message);
    } else toast.error('Failed to add trip');

    return null;
  }
}

export async function handleUpdateBusinessSettings(
  businessId: string,
  payload: UpdateBusinessSettingsPayload,
) {
  try {
    const body = await updateBusinessSettingsSchema.noUnknown().validate(
      { ...payload, maxApptsPerDay: payload.maxApptsPerDay || null },
      {
        stripUnknown: true,
      },
    );

    const bookInAdvanceMinutes = body.bookInAdvanceHours * 60;

    const updatedSettings = await updateBusinessSettings(businessId, {
      ...body,
      bookInAdvanceMinutes,
    });

    if (updatedSettings) {
      toast.success('Default artist settings updated!');
    }

    return updatedSettings;
  } catch (error) {
    if (error instanceof ValidationError) {
      toast.error(error.message);
    } else toast.error('Failed to update settings');

    return null;
  }
}

export async function handleUpsertBusinessService(
  businessId: string,
  service: BusinessService | null,
  payload: UpsertBusinessServiceInitValues,
  intakeQuestions?: { questionId: string; enabled: boolean }[],
) {
  try {
    const body = await upsertBusinessServiceFormSchema
      .noUnknown()
      .validate(payload, {
        stripUnknown: true,
      });

    const minimumAgeInMonths = body.minimumAge
      ? body.minimumAgeUnit === 'months'
        ? body.minimumAge
        : body.minimumAge * 12
      : 0;

    const employeeCommissions = payload.employeeCommissions
      ?.filter(c => c.enabled)
      .map(c => ({ employeeId: c.employeeId, rate: c.rate / 100 }));

    const finalBody = {
      ...body,
      minimumAgeInMonths,
      enableDirectBookings:
        body.onlineBookingsBehavior ===
        ONLINE_BOOKINGS_BEHAVIOR.DIRECT_BOOKINGS,
      enableBookingRequests:
        body.onlineBookingsBehavior ===
        ONLINE_BOOKINGS_BEHAVIOR.BOOKING_REQUESTS,
      enableCheckIn: body.enableCheckIn,
      intakeQuestions,
      employeeCommissions,
    };

    const validatedFinalBody = await upsertBusinessServiceSchema
      .noUnknown()
      .validate(finalBody, {
        stripUnknown: true,
      });

    const serviceResponse = service
      ? await updateBusinessService(
          businessId,
          service.serviceId,
          validatedFinalBody,
        )
      : await createBusinessService(businessId, validatedFinalBody);

    if (serviceResponse) {
      toast.success(`Service ${service ? 'updated' : 'created'}!`);
    }

    return serviceResponse;
  } catch (error) {
    if (error instanceof ValidationError) {
      toast.error(error.message);
    } else toast.error(`Failed to ${service ? 'update' : 'create'} service`);

    return null;
  }
}

export function getBusinessTripName(trip: Trip) {
  return `${trip.city}, ${trip.state} ${format(
    new Date(trip.startDateTime),
    'MM/dd/yyyy',
  )} - ${format(new Date(trip.endDateTime), 'MM/dd/yyyy')}`;
}

export function getBusinessShopUrl(
  business: Pick<Business, 'urlPath'>,
  path = '',
) {
  const cleanedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${CUSTOMER_WEB_URL}/shop/${business.urlPath}${
    cleanedPath ? `/${cleanedPath}` : ''
  }`;
}

export function getBusinessArtistUrl(
  employee: Pick<Employee | Employees | EmployeeToBusiness, 'urlPath'>,
) {
  if (!employee || !employee?.urlPath) return '';
  return `${CUSTOMER_WEB_URL}/artist/${employee.urlPath}`;
}

export function filterBookableServices(services?: Service[]) {
  return services?.filter(service => service.enableDirectBookings);
}

export function getBusinessCurrency(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _business: Business | LoadedUser['business'] | null | undefined,
) {
  return 'USD';
}

export function canIndependentManageShop(user: LoadedUser) {
  return (
    user?.business?.type === 'INDEPENDENT' &&
    (user.business.employees.length < 2 || user.role === 'SHOP_MANAGER')
  );
}

export function canManageStripeAccount(user: LoadedUser | null | undefined) {
  return user?.business?.type === 'STUDIO' && isSudoManager(user);
}

export const toFormReschedulePolicy = (
  policy: ReschedulePolicy | null | undefined,
): ReschedulePolicy =>
  policy === ReschedulePolicy.CLOSED
    ? ReschedulePolicy.CLOSED
    : ReschedulePolicy.OPEN;

export const allowsReschedule = (policy: ReschedulePolicy | null | undefined) =>
  policy !== ReschedulePolicy.CLOSED;

export const fromFormReschedulePolicy = (
  formValue: ReschedulePolicy,
  current: ReschedulePolicy | null | undefined,
): ReschedulePolicy => {
  if (formValue === ReschedulePolicy.CLOSED) return ReschedulePolicy.CLOSED;
  return current === ReschedulePolicy.REQUESTS
    ? ReschedulePolicy.REQUESTS
    : ReschedulePolicy.OPEN;
};
